import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import BaseBox from '../components/BaseBox';
import {
  selectVariant,
  setHardSelectedVariant,
  useSliceSectionSelector,
  Variant
} from '@/redux/slices/pdpSlice';
import { AppDispatch } from '@/redux/store';
import SingleRecording from './components/SingleRecording';
import Multiplerecordings from './components/Multiplerecordings';
import UpcomingLiveCard, { UpcomingLiveCardProps } from './components/UpcomingLiveCard';
import PanelButton from '@/components/ui/PDP/ECommBox/components/PanelButton';
import { useAddToCart } from '@/hooks/shared/useAddToCart';
import { useLaunch } from '@/hooks/shared/useLaunch';
import useInViewport from '@/hooks/useInViewport';
import Button from '@/components/ui/Buttons/Button';
import ProgramMaterialsModal from '../../Modals/ProgramMaterialsModal';
import { MaterialsLabels } from '../../labels';
import { v4 as uuid4 } from 'uuid';
import { AnalyticsContext } from '@/analytics/constants';
import Loading from './components/Loading';
import { getDiscountAdviceMessage } from '../../helpers';
import { useAuth } from 'react-oidc-context';
import { trackReadTranscriptEvent } from '@/analytics/library';
import { useFetchAnalyticsItems } from '@/analytics/useFetchAnalyticsItems';
import { useContentOptOut } from '@/hooks/PDP/useContentOptOut';
import { usePDPSliceSelectorContext } from '@/hooks/shared/usePDPSliceSelector';
import { useRegistrations } from '@/hooks/shared/useRegistrations';
import { useNavigate } from 'react-router';
import { isDateInPast, isExpirationWithinDayRange } from '@/utils/dateHelpers';
import { transformUrlToPath } from '@/utils/helpers';
import { ItemListAnalyticsModel } from '@/@types/client-api';

export interface OnDemandProps {
  labels: {
    watchNow: string;
    recorded: string;
    launchButton: string;
    purchaseButton: string;
    noSelectionMsg: string;
    privilegedMembers?: string;
    seciWsDiscountAdvice?: string;
    extraMessage?: string;
    programId: string;
    upcomingLiveLabel?: string;
    materials?: string;
    transcript?: string;
    poecExpiringSoonDayRange?: number;
    poecExtensionUrl?: string;
    poecExtendAccessPurchaseButton?: string;
    poecExtendAccessToast?: string;
  };
  upcomingLiveProgram?: UpcomingLiveCardProps;
  isMobile?: boolean;
  title?: string;
  onClosePanel?: () => void;
  seeDetailsLink?: string;
  loadingCatalogRelations?: boolean;
  loadingProgramSegments?: boolean;
  fetchingLibrary?: boolean;
  context?: AnalyticsContext;
  creditTrackerCode?: string;
  itemListAnalyticsModel?: ItemListAnalyticsModel;
}

const OnDemand: React.FC<OnDemandProps> = ({
  labels,
  isMobile = false,
  upcomingLiveProgram,
  title,
  onClosePanel,
  seeDetailsLink,
  loadingProgramSegments,
  fetchingLibrary,
  context,
  creditTrackerCode,
  itemListAnalyticsModel
}) => {
  const { sliceName } = usePDPSliceSelectorContext();

  const materialModalId = useMemo(() => `material-modal-${uuid4()}`, []);

  const dispatch = useDispatch<AppDispatch>();
  const auth = useAuth();

  const [displaySelectionError, setDisplaySelectionError] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const catalogRelations = useSliceSectionSelector(sliceName, s => s.catalogRelations);
  const selectedVariant = useSliceSectionSelector(sliceName, s => s.selectedVariant);
  const hardSelected = useSliceSectionSelector(sliceName, s => s.hardSelected);

  const nonExpiredVariants = useMemo(
    () =>
      catalogRelations?.variants?.filter(v => !v.isExpired || (v.itemClass === 160 && v.purchased)),
    [catalogRelations?.variants]
  );

  const shouldShowExtendAccessButtonForPOEC = useMemo(() => {
    const isEligibleForExtension =
      selectedVariant?.poecExtensionData?.isPoecOdVariantEligibleForExtension;
    const poecPurchaseItem = selectedVariant?.poecExtensionData?.poecExtensionTargetItem;
    const isExpiringSoon = isExpirationWithinDayRange(
      poecPurchaseItem?.accessExpirationDate,
      labels.poecExpiringSoonDayRange
    );
    const isExpired = isDateInPast(poecPurchaseItem?.accessExpirationDate);
    return isEligibleForExtension && (isExpiringSoon || isExpired);
  }, [
    labels.poecExpiringSoonDayRange,
    selectedVariant?.poecExtensionData?.isPoecOdVariantEligibleForExtension,
    selectedVariant?.poecExtensionData?.poecExtensionTargetItem
  ]);

  const [showCheck, setShowCheck] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showError, setShowError] = useState(false);

  const [stickyLaunchButtonRef, isInView, position] = useInViewport<HTMLButtonElement>();

  const withinPanel = !!onClosePanel;
  const { getAnalyticItem } = useFetchAnalyticsItems();

  const {
    addToCart,
    isSuccess: isCartSuccess,
    reset: cartReset,
    isLoading: isCartLoading,
    isError: isCartError
  } = useAddToCart(context, creditTrackerCode, itemListAnalyticsModel);
  const {
    launch,
    isLoading: isLaunchLoading,
    isSuccess: isLaunchSuccess,
    isError: isLaunchError
  } = useLaunch(context, creditTrackerCode, itemListAnalyticsModel);

  // $0 purchase of POEC OD Extension is more similar to a $0 registration, re-use this hook so we continue to capture $0 purchase analytics and other behaviors.
  const {
    placeRegistration: placePoecExtensionPurchase,
    isLoading: isPoecExtensionPurchaseLoading,
    isSuccess: isPoecExtensionPurchaseSuccess,
    isError: isPoecExtensionPurchaseError
  } = useRegistrations(
    context,
    creditTrackerCode,
    selectedVariant?.poecExtensionData?.isPoecExtensionVariant ?? false,
    labels.poecExtendAccessToast,
    itemListAnalyticsModel
  );

  const navigate = useNavigate();

  useEffect(() => {
    if (nonExpiredVariants?.length === 1) {
      dispatch(setHardSelectedVariant({ sliceName, variant: nonExpiredVariants[0] }));
    } else {
      // select first selected variant, if nothing has been hard-selected yet
      if (hardSelected) return;
      const selectedVariant = nonExpiredVariants?.find(variant => variant.purchased);

      if (selectedVariant) {
        dispatch(selectVariant({ sliceName, variant: selectedVariant }));
      }
    }
  }, [nonExpiredVariants, dispatch, hardSelected, sliceName]);

  useEffect(() => {
    if (isCartSuccess || isLaunchSuccess || isPoecExtensionPurchaseSuccess) {
      setShowCheck(true);
      setShowLoading(false);
      setShowError(false);
      setTimeout(() => {
        setShowCheck(false);
        if (isCartSuccess) {
          cartReset();
        }
        if (
          isPoecExtensionPurchaseSuccess &&
          !!selectedVariant?.poecExtensionData?.poecExtensionTargetItem?.url
        ) {
          // Redirect to POEC OD after purchase so user can launch from there.
          const path = transformUrlToPath(
            selectedVariant?.poecExtensionData?.poecExtensionTargetItem?.url
          );
          navigate(path);
        }
      }, 1000);
      if (isCartSuccess && withinPanel) {
        onClosePanel?.();
      }
    }
    if (isCartLoading || isLaunchLoading || isPoecExtensionPurchaseLoading) {
      setShowLoading(true);
    }

    if (isCartError || isLaunchError || isPoecExtensionPurchaseError) {
      setShowError(true);
      setShowLoading(false);
    }
  }, [
    cartReset,
    isCartError,
    isCartLoading,
    isCartSuccess,
    isLaunchError,
    isLaunchLoading,
    isLaunchSuccess,
    isPoecExtensionPurchaseError,
    isPoecExtensionPurchaseLoading,
    isPoecExtensionPurchaseSuccess,
    navigate,
    onClosePanel,
    selectedVariant?.poecExtensionData?.poecExtensionTargetItem?.url,
    withinPanel
  ]);

  const buttonProps = useMemo(() => {
    const isPurchased = selectedVariant?.purchased;
    const canLaunch = (isPurchased || !selectedVariant?.retail) && auth.isAuthenticated;
    const isPoecExtension = selectedVariant?.poecExtensionData?.isPoecExtensionVariant;
    const showPrice =
      !isPurchased &&
      (!isPoecExtension || (isPoecExtension && selectedVariant.discountPrice !== 0));
    const label = !isPoecExtension
      ? canLaunch
        ? labels.launchButton
        : labels.purchaseButton
      : labels.poecExtendAccessPurchaseButton;

    return {
      label,
      price: showPrice ? selectedVariant?.price : undefined,
      strikePrice: showPrice ? selectedVariant?.originalPrice : undefined,
      purchased: isPurchased,
      canLaunch
    };
  }, [
    auth.isAuthenticated,
    labels.launchButton,
    labels.poecExtendAccessPurchaseButton,
    labels.purchaseButton,
    selectedVariant
  ]);

  const handleSelect = useCallback(
    (variant: Variant) => {
      setDisplaySelectionError(false);
      dispatch(selectVariant({ sliceName, variant }));
    },
    [dispatch, sliceName]
  );

  const handleSubmit = useCallback(async () => {
    setDisplaySelectionError(!hardSelected);

    if (hardSelected) {
      if (
        selectedVariant?.poecExtensionData?.isPoecExtensionVariant &&
        selectedVariant?.discountPrice === 0
      ) {
        placePoecExtensionPurchase(selectedVariant);
        return;
      }
      if (!buttonProps.canLaunch) {
        addToCart({ code: selectedVariant?.code });
      } else {
        launch({
          code: selectedVariant?.code,
          isLiveUnregLaunch: false,
          alreadyPurchasedLaunchUrl: selectedVariant?.purchased
            ? selectedVariant?.launchUrl
            : undefined
        });
      }
    }
  }, [
    addToCart,
    buttonProps.canLaunch,
    hardSelected,
    launch,
    placePoecExtensionPurchase,
    selectedVariant
  ]);

  const handleStickyButton = useCallback(() => {
    if (nonExpiredVariants && nonExpiredVariants.length > 1) {
      setShowModal(true);
    } else {
      handleSubmit();
    }
  }, [nonExpiredVariants, handleSubmit]);

  const [openProgramMaterials, setOpenProgramMaterials] = useState(false);
  const getProgramMaterialsProps = useCallback(() => {
    return {
      programCode: selectedVariant?.code,
      programTitle: selectedVariant?.displayName ?? '',
      materials: selectedVariant?.materials ?? [],
      open: openProgramMaterials,
      setOpen: setOpenProgramMaterials,
      labels: MaterialsLabels
    };
  }, [
    openProgramMaterials,
    selectedVariant?.displayName,
    selectedVariant?.materials,
    selectedVariant?.code
  ]);

  const extendAccessLinkButton = useMemo(() => {
    if (shouldShowExtendAccessButtonForPOEC && labels?.poecExtensionUrl) {
      const path = transformUrlToPath(labels.poecExtensionUrl);
      const target = `${window.location.origin}${path}`;
      return (
        <Button
          label="Extend access"
          iconRight="chevron-right"
          color="outline-black"
          size="large"
          className="mt-4 w-full !justify-center"
          href={target}
        />
      );
    }
  }, [labels?.poecExtensionUrl, shouldShowExtendAccessButtonForPOEC]);

  const secondaryButton = useMemo(() => {
    if (extendAccessLinkButton) return extendAccessLinkButton; // POEC OD Extension case. POEC OD doesn't have materials so it's fine.
    if (hardSelected && selectedVariant?.hasMaterials) {
      return (
        <Button
          label={labels.materials}
          color="outline-black"
          size="large"
          className="mt-4 w-full"
          onClick={() => setOpenProgramMaterials(true)}
          aria-controls={materialModalId}
        />
      );
    }
    return undefined;
  }, [
    extendAccessLinkButton,
    hardSelected,
    selectedVariant?.hasMaterials,
    labels.materials,
    materialModalId
  ]);

  const trackEvent = useCallback(
    async (code: string, context?: AnalyticsContext) => {
      const data = await getAnalyticItem(code);
      if (data) {
        trackReadTranscriptEvent(data, context);
      }
    },
    [getAnalyticItem]
  );

  const tertiaryButton = useMemo(() => {
    if (hardSelected && selectedVariant?.transcriptUrl?.length) {
      return (
        <Button
          label={labels.transcript}
          iconRight="link-out"
          color="outline-black"
          size="large"
          className="mt-4 w-full !justify-center"
          href={selectedVariant?.transcriptUrl}
          target="_blank"
          onClick={() => trackEvent(selectedVariant?.code ?? '', context)}
        />
      );
    }
  }, [
    context,
    hardSelected,
    labels.transcript,
    selectedVariant?.code,
    selectedVariant?.transcriptUrl,
    trackEvent
  ]);

  const panelButtonProps = useMemo(() => {
    const { canLaunch: _canLaunch, ...props } = buttonProps;
    return props;
  }, [buttonProps]);

  const cartSuccessLabel = !buttonProps.canLaunch ? buttonProps.label : undefined;

  const primaryButton = useMemo(() => {
    const isActionLoading = showLoading || !!fetchingLibrary;

    return (
      <PanelButton
        ref={stickyLaunchButtonRef}
        isSuccess={showCheck}
        isLoading={isActionLoading}
        isError={showError}
        successLabel={cartSuccessLabel}
        onClick={handleSubmit}
        {...panelButtonProps}
      />
    );
  }, [
    cartSuccessLabel,
    fetchingLibrary,
    handleSubmit,
    panelButtonProps,
    showCheck,
    showError,
    showLoading,
    stickyLaunchButtonRef
  ]);

  const { contentOptOut } = useContentOptOut();
  const hasRequiredData = catalogRelations?.variants?.[0] || selectedVariant;
  // remove loadingCatalogRelations for when a possible race conditions happens
  if (!hasRequiredData) {
    return <Loading isMobile={isMobile} />;
  }

  return (
    <>
      <ProgramMaterialsModal
        customModalId={materialModalId}
        {...getProgramMaterialsProps()}
        context={context}
      />
      <BaseBox
        sticky={!isMobile}
        showModal={showModal}
        onCloseModal={() => setShowModal(false)}
        withinPanel={withinPanel}
        data-component={'Ondemand'}
      >
        {withinPanel && (
          <BaseBox.PanelHeader
            productCode={selectedVariant?.code}
            title={title}
            seeDetailsLink={seeDetailsLink}
            context={context}
          />
        )}
        <BaseBox.Content withinPanel={withinPanel}>
          {!withinPanel && <h3 className="text-2 mb-6 font-bold">{labels.watchNow}</h3>}

          {nonExpiredVariants?.length === 1 && (
            <SingleRecording
              labels={labels}
              variant={nonExpiredVariants[0]}
              onClick={handleSubmit}
              isSuccess={showCheck}
              isLoading={showLoading || !!fetchingLibrary}
              isError={showError}
              loadingProgramSegments={loadingProgramSegments}
            />
          )}
          {nonExpiredVariants && nonExpiredVariants.length > 1 && (
            <Multiplerecordings
              labels={labels}
              onSelect={handleSelect}
              loadingProgramSegments={loadingProgramSegments}
            />
          )}
        </BaseBox.Content>

        <BaseBox.Footer
          primaryButton={primaryButton}
          secondaryButton={secondaryButton}
          tertiaryButton={tertiaryButton}
          noSelectionError={displaySelectionError && labels.noSelectionMsg}
          discountAdviceMessage={getDiscountAdviceMessage(selectedVariant, labels, contentOptOut)}
          extraMessage={labels.extraMessage}
          codeLabel={labels.programId}
          codeValue={selectedVariant?.code}
          withinPanel={withinPanel}
        ></BaseBox.Footer>

        {!!upcomingLiveProgram && <UpcomingLiveCard {...upcomingLiveProgram} />}

        {isMobile && (
          <>
            {nonExpiredVariants && nonExpiredVariants.length > 1 && (
              <BaseBox.Modal className="flex h-screen flex-col">
                <BaseBox.Content inModal>
                  <Multiplerecordings labels={labels} onSelect={handleSelect} />
                </BaseBox.Content>
                <BaseBox.Footer
                  primaryButton={primaryButton}
                  noSelectionError={displaySelectionError && labels.noSelectionMsg}
                  discountAdviceMessage={getDiscountAdviceMessage(
                    selectedVariant,
                    labels,
                    contentOptOut
                  )}
                  extraMessage={labels.extraMessage}
                  codeLabel={labels.programId}
                  codeValue={selectedVariant?.code}
                ></BaseBox.Footer>
              </BaseBox.Modal>
            )}
            <BaseBox.StickyButton sticky={!isInView && position === 'above'}>
              <PanelButton
                isSuccess={showCheck}
                isLoading={showLoading || !!fetchingLibrary}
                isError={showError}
                successLabel={cartSuccessLabel}
                onClick={handleStickyButton}
                {...panelButtonProps}
              />
            </BaseBox.StickyButton>
          </>
        )}
      </BaseBox>
    </>
  );
};

export default OnDemand;
